import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface ConfirmDialogControl<T> {
  readonly show: boolean;
  /** The last opened content; kept while the dialog closes so its exit animation has text. */
  readonly content?: T;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * A dialog driven by a promise: `open(content)` shows it and resolves `true` on confirm and
 * `false` on cancel or close. Render the dialog with `dialog`. Opening it again, or unmounting,
 * resolves the pending promise with `false`, so a caller never stays blocked.
 */
export function useConfirmDialog<T>() {
  const [state, setState] = useState<{ show: boolean; content?: T }>({ show: false });
  const resolveRef = useRef<(confirmed: boolean) => void>();

  const settle = useCallback((confirmed: boolean) => {
    setState(current => ({ ...current, show: false }));
    const resolve = resolveRef.current;
    resolveRef.current = undefined;
    resolve?.(confirmed);
  }, []);

  useEffect(() => () => resolveRef.current?.(false), []);

  const open = useCallback((content: T): Promise<boolean> => {
    resolveRef.current?.(false);
    return new Promise<boolean>(resolve => {
      resolveRef.current = resolve;
      setState({ show: true, content });
    });
  }, []);

  const onConfirm = useCallback(() => settle(true), [settle]);
  const onCancel = useCallback(() => settle(false), [settle]);

  const dialog = useMemo<ConfirmDialogControl<T>>(
    () => ({ show: state.show, content: state.content, onConfirm, onCancel }),
    [onCancel, onConfirm, state]
  );

  return { open, dialog };
}
